export const COOKIE_PREF_KEY = "signacole_cookie_consent_v1";
export const COOKIE_SETTINGS_EVENT = "signacole-cookie-settings";
export const GA_ID = "G-XW7BZ6D61J";

export type CookieConsent = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

type GtagWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

const win = () => window as GtagWindow;

const gtag = () => win().gtag;

let analyticsStarted = false;

const expire = (name: string) => {
  const host = window.location.hostname;
  [
    `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`,
    `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${host}`,
    `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${host}`,
  ].forEach((cookie) => {
    document.cookie = cookie;
  });
};

const expireAnalytics = () => {
  const names = document.cookie
    .split(";")
    .map((part) => part.split("=")[0].trim())
    .filter(Boolean);
  const known = ["_ga", "_gid", "_gat", `_gat_gtag_${GA_ID.replace(/-/g, "_")}`, `_ga_${GA_ID.replace("G-", "")}`];
  names.concat(known).forEach(expire);
};

export const parseConsent = (raw: string | null): CookieConsent | null => {
  if (!raw) return null;
  try {
    const data = JSON.parse(raw) as Partial<CookieConsent>;
    if (!data || typeof data !== "object") return null;
    return {
      necessary: true,
      analytics: !!data.analytics,
      marketing: !!data.marketing,
    };
  } catch {
    return null;
  }
};

export const loadConsent = (): CookieConsent | null => {
  try {
    return parseConsent(localStorage.getItem(COOKIE_PREF_KEY));
  } catch {
    return null;
  }
};

const startAnalytics = () => {
  if (analyticsStarted || typeof window === "undefined") return;
  analyticsStarted = true;
  const w = win();
  w.dataLayer = w.dataLayer || [];
  w.gtag = function gtagFn(..._args: unknown[]) {
    w.dataLayer!.push(arguments);
  };
  w.gtag("js", new Date());
  w.gtag("config", GA_ID);
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
};

export const applyConsent = (consent: CookieConsent) => {
  if (consent.analytics) {
    startAnalytics();
    gtag()?.("consent", "update", {
      analytics_storage: "granted",
      ad_storage: consent.marketing ? "granted" : "denied",
    });
    return;
  }
  gtag()?.("consent", "update", {
    analytics_storage: "denied",
    ad_storage: "denied",
  });
  expireAnalytics();
};

export const saveConsent = (next: { analytics: boolean; marketing: boolean }): CookieConsent => {
  const consent: CookieConsent = {
    necessary: true,
    analytics: next.analytics,
    marketing: next.marketing,
  };
  try {
    localStorage.setItem(COOKIE_PREF_KEY, JSON.stringify(consent));
    localStorage.removeItem("signacole-cookies");
  } catch {
    /* ignore */
  }
  applyConsent(consent);
  return consent;
};

export const openCookieSettings = () => {
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
};
